import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import platformStatsService from '../services/platformStatsService';

const TractionSection = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animatedNumbers, setAnimatedNumbers] = useState({
    brands: 0,
    influencers: 0,
    campaigns: 0,
    reviews: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      const platformStats = await platformStatsService.getPlatformStats();

      setStats(platformStats);

      // Animate numbers
      animateNumbers(platformStats);
    } catch (error) {
      console.error('Error fetching platform stats:', error);
      // Set default values on error
      setStats({
        totalBrands: 0,
        totalInfluencers: 0,
        totalCampaigns: 0,
        totalReviews: 0,
        growth: { brands: 0, influencers: 0, campaigns: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const animateNumbers = (data) => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const interval = duration / steps;
    
    let currentStep = 0;
    
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setAnimatedNumbers({
        brands: Math.floor(data.totalBrands * progress),
        influencers: Math.floor(data.totalInfluencers * progress),
        campaigns: Math.floor(data.totalCampaigns * progress),
        reviews: Math.floor(data.totalReviews * progress)
      });
      
      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedNumbers({
          brands: data.totalBrands,
          influencers: data.totalInfluencers,
          campaigns: data.totalCampaigns,
          reviews: data.totalReviews
        });
      }
    }, interval);
  };

  const StatCard = ({ label, value }) => (
    <div className="relative bg-white rounded-xl border border-gray-200 p-6 overflow-hidden">
      {/* Dot-grid background */}
      <div
        className="absolute inset-0 opacity-[0.4] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
          backgroundSize: '16px 16px'
        }}
      />
      {/* Content */}
      <div className="relative">
        <div className="text-2xl md:text-3xl font-bold text-gray-900">
          {value.toLocaleString()}+
        </div>
        <div className="mt-2 text-sm text-gray-500">{label}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <motion.div 
            className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Platform Growth
          </motion.div>
          
          <h2 className="text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl mb-4">
            Join Our Growing Community
          </h2>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
            Real-time metrics from brands and influencers collaborating on Engaged Manila
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <StatCard
            label="Active Brands"
            value={animatedNumbers.brands}
          />
          <StatCard
            label="Influencers"
            value={animatedNumbers.influencers}
          />
          <StatCard
            label="Campaigns Launched"
            value={animatedNumbers.campaigns}
          />
          <StatCard
            label="Reviews Published"
            value={animatedNumbers.reviews}
          />
        </div>

      </div>
    </div>
  );
};

export default TractionSection;
